/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import axios from 'axios';
import get from 'lodash/get';
import { SecurityHelper } from 'sarsaparilla';
import { gnoment } from 'shared-ui/gnoment';
import { printBlob } from 'ui-permit/src/utils/utilsNew';
import { loadContentIntoPermit } from 'ui-permit/src/utils/enrich-backend-data';
import { PERMIT_CONTENT_ENDPOINT, PERMIT_ENDPOINT_V2 } from 'ui-permit/src/constants';
import { permitContentSelector } from 'ui-permit/shared-components/slices/permitContent';
import * as enrichment from '../utils/dataUtils';
import { salesChannelTypes, getIssuanceDivision } from '../utils/permitUtilsNew';
import * as types from '../constants';

function prepareIssuance(iss) {
    const issuance = iss;
    // ensure start_date is gnoment and utc offset
    if (issuance.start_date) {
        if (!gnoment.isGnoment(issuance.start_date)) {
            issuance.start_date = gnoment(issuance.start_date);
        }
        issuance.start_date = issuance.start_date
            .utcOffset(0)
            .startOf('day')
            .toISOString();
    }

    // ensure start_date is gnoment and utc offset
    if (issuance.end_date) {
        if (!gnoment.isGnoment(issuance.end_date)) {
            issuance.end_date = gnoment(issuance.end_date);
        }
        issuance.end_date = issuance.end_date.utcOffset(0).startOf('day').toISOString();
    }

    if (issuance.lottery_preferences) {
        issuance.lottery_preferences = issuance.lottery_preferences.map((pref) => {
            const x = pref;
            if (gnoment.isGnoment(x.preferred_date)) {
                const formattedDate = x.preferred_date
                    .utcOffset(0)
                    .startOf('day')
                    .toISOString();
                x.preferred_date = formattedDate;
            }
            return x;
        });
    }

    issuance.sales_channel_type = salesChannelTypes.get(types.ONLINE);
    return issuance;
}

export function fetchPermit(permitId) {
    return (dispatch, getState) => {
        const permitMapping = permitContentSelector.permitMapping(getState());
        const apiCalls = [
            axios.get(PERMIT_CONTENT_ENDPOINT(permitId)),
            axios.get(PERMIT_ENDPOINT_V2(permitId, permitMapping)),
        ];

        return Promise.all(apiCalls)
            .then(([pcRes, pRes]) => {
                const { payload: pcPayload } = pcRes.data;
                const { payload: pPayload } = pRes.data;
                const permit = pRes
                    ? loadContentIntoPermit(pPayload, pcPayload)
                    : pcPayload;

                const transformedPermit = enrichment.transformPermit(permit);

                dispatch({
                    type: types.GET_PERMIT,
                    payload: transformedPermit,
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_PERMIT_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                    });
                }
            });
    };
}

export function fetchAggregateSeason(permitId, forLottery = false) {
    return (dispatch) => {
        return axios
            .get(
                `${process.env.API}/permits/${permitId}/getAggregateSeason?for_lottery=${forLottery}`
            )
            .then((success) => {
                dispatch({
                    type: types.GET_AGGREGATE_SEASON_RULES,
                    payload: success.data.payload,
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_AGGREGATE_SEASON_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                    });
                }
            });
    };
}

export function fetchLottery(lotteryId) {
    return function (dispatch) {
        return axios
            .get(`${process.env.API}/permits/0/lotteries/${lotteryId}`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then((response) => {
                dispatch({
                    type: types.GET_LOTTERY,
                    payload: response.data.payload,
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_LOTTERY_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                    });
                }
            });
    };
}

export function fetchCooperators(permitId) {
    return function (dispatch) {
        return axios
            .get(`${process.env.API}/permits/${permitId}/cooperators`)
            .then((success) => {
                dispatch({
                    type: types.GET_COOPERATORS,
                    payload: success.data.payload,
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_COOPERATORS_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                    });
                }
            });
    };
}

export function fetchCooperator(permitId, cooperatorId) {
    return function (dispatch) {
        return axios
            .get(`${process.env.API}/permits/${permitId}/cooperators/${cooperatorId}`)
            .then((success) => {
                dispatch({
                    type: types.GET_COOPERATOR,
                    payload: success.data.payload,
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_COOPERATOR_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                    });
                }
            });
    };
}

// TODO: if it ever wants to use captcha, needs to get the token from recaptchav3Lib in sarsa
export function postIssuance(issuance, doRedirect, recaptcha = {}) {
    const request = {
        gate_a: recaptcha,
        issuance,
    };

    return (dispatch) => {
        dispatch({
            type: types.SET_ISSUANCE_LOADING,
            issuanceLoading: true,
        });
        return axios
            .post(
                `${process.env.API}/permits/${issuance.permit_id}/issuances`,
                JSON.stringify(request),
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                dispatch({
                    type: types.SET_ISSUANCE_LOADING,
                    issuanceLoading: false,
                });
                dispatch({ type: types.POST_ISSUANCE, payload: response.data.payload });
                if (doRedirect) {
                    window.location.assign('/cart');
                }
                return response;
            })
            .catch((error) => {
                dispatch({
                    type: types.SET_ISSUANCE_LOADING,
                    issuanceLoading: false,
                });
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.POST_ISSUANCE_ERROR,
                        payload: 'error while posting issuance data',
                        error: error.response.data.error,
                    });
                } else {
                    dispatch({
                        type: types.POST_ISSUANCE_ERROR,
                        payload: 'error while posting issuance data',
                        error: `${error}`,
                    });
                }
                // Re-reject so callers can branch on the reCAPTCHA challenge signal.
                return Promise.reject(error);
            });
    };
}

export function updateIssuance(issuance) {
    const preparedIssuance = prepareIssuance(issuance);
    return (dispatch) => {
        dispatch({
            type: types.SET_ISSUANCE_LOADING,
            issuanceLoading: true,
        });
        return axios
            .post(
                `${process.env.API}/permits/${preparedIssuance.permit_id}/issuances/${preparedIssuance.id}`,
                JSON.stringify(preparedIssuance),
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                dispatch({
                    type: types.SET_ISSUANCE_LOADING,
                    issuanceLoading: false,
                });
                dispatch({ type: types.UPDATE_ISSUANCE, payload: response.data.payload });
            })
            .catch((error) => {
                dispatch({
                    type: types.SET_ISSUANCE_LOADING,
                    issuanceLoading: false,
                });
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.UPDATE_ISSUANCE_ERROR,
                        payload: 'error while posting issuance data',
                        error: error.response.data,
                    });
                }
            });
    };
}

export function cancelIssuance(issuanceId, permitId) {
    // Note: do not actually need the body of the request (right now contains issuanceId)
    return (dispatch) =>
        axios
            .post(
                `${process.env.API}/permits/${permitId}/issuances/${issuanceId}/cancel`,
                JSON.stringify(issuanceId),
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                dispatch({ type: types.CANCEL_ISSUANCE, payload: response.data.payload });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.CANCEL_ISSUANCE_ERROR,
                        payload: 'error while cancelling reservation',
                        error: error.response.data,
                    });
                }
            });
}

export function addItem(itemType, item, doRedirect, recaptcha) {
    const preparedIssuance = prepareIssuance(item);
    return postIssuance(preparedIssuance, doRedirect, recaptcha);
}

export async function addItems(items, recaptcha = {}) {
    const issuanceIds = [];
    const promises = items.map((item) => {
        const issuance = prepareIssuance(item);
        const request = { gate_a: recaptcha, issuance };
        return axios.post(
            `${process.env.API}/permits/${issuance.permit_id}/issuances`,
            JSON.stringify(request),
            { headers: { Authorization: SecurityHelper.getAuthHeader() } }
        );
    });
    const results = await Promise.all(promises);
    for (const result of results) {
        const response = await result;
        issuanceIds.push(response.data.payload.id);
    }
    return issuanceIds;
}

export async function updateItems(items) {
    const issuanceIds = [];
    const promises = items.map((item) => {
        const issuance = prepareIssuance(item);
        return axios.post(
            `${process.env.API}/permits/${issuance.permit_id}/issuances/${issuance.id}`,
            JSON.stringify(issuance),
            { headers: { Authorization: SecurityHelper.getAuthHeader() } }
        );
    });
    const results = await Promise.all(promises);
    for (const result of results) {
        const response = await result;
        if (
            response.data.payload.status !== types.ISSUANCE_UPDATE_SUCCESSFUL &&
            response.data.payload.status !== types.ISSUANCE_UPDATE_REQUIRES_PAYMENT
        ) {
            throw new Error('Could not update issuance');
        }
        issuanceIds.push(response.data.payload.issuance.id);
    }
    return issuanceIds;
}

export function saveFilters(filters) {
    return function (dispatch) {
        return dispatch({
            type: types.SAVE_FILTERS,
            payload: filters,
        });
    };
}

export function createLottery(lottery) {
    return (dispatch) =>
        axios
            .post(
                `${process.env.API}/permits/${lottery.permit_id}/lotteries`,
                JSON.stringify(lottery),
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                dispatch({ type: types.POST_LOTTERY, payload: response.data.payload });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.POST_LOTTERY_ERROR,
                        payload: 'error while creating lottery',
                        error: error.response.data,
                    });
                }
            });
}

export function sendCurrentDate(date) {
    return function (dispatch) {
        return dispatch({
            type: types.SEND_CURRENT_DATE,
            payload: date,
        });
    };
}

export function fetchIssuance(issuanceId, permitId) {
    return function (dispatch) {
        dispatch({
            type: types.SET_ISSUANCE_LOADING,
            issuanceLoading: true,
        });
        return axios
            .get(
                `${process.env.API}/permits/${permitId}/issuances/${issuanceId}/account`,
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((success) => {
                dispatch({
                    type: types.SET_ISSUANCE_LOADING,
                    issuanceLoading: false,
                });
                dispatch({
                    type: types.GET_ISSUANCE,
                    payload: Object.assign(success.data.payload),
                });
            })
            .catch((error) => {
                dispatch({
                    type: types.SET_ISSUANCE_LOADING,
                    issuanceLoading: false,
                });
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_ISSUANCE_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                        error_code: error.response.status,
                    });
                }
            });
    };
}

export async function fetchIssuanceAsync(issuanceId, permitId) {
    const response = await axios.get(
        `${process.env.API}/permits/${permitId}/issuances/${issuanceId}/account`,
        { headers: { Authorization: SecurityHelper.getAuthHeader() } }
    );
    return response.data.payload;
}

export function fetchUnpaidIssuance(issuanceId, permitId) {
    return function (dispatch) {
        dispatch({
            type: types.SET_ISSUANCE_LOADING,
            issuanceLoading: true,
        });
        return axios
            .get(
                `${process.env.API}/permits/${permitId}/issuances/${issuanceId}/unpaid`,
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((success) => {
                dispatch({
                    type: types.SET_ISSUANCE_LOADING,
                    issuanceLoading: false,
                });
                dispatch({
                    type: types.GET_ISSUANCE,
                    payload: Object.assign(success.data.payload),
                });
            })
            .catch((error) => {
                dispatch({
                    type: types.SET_ISSUANCE_LOADING,
                    issuanceLoading: false,
                });
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_ISSUANCE_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                        error_code: error.response.status,
                    });
                }
            });
    };
}

export async function fetchUnpaidIssuanceAsync(issuanceId, permitId) {
    const response = await axios.get(
        `${process.env.API}/permits/${permitId}/issuances/${issuanceId}/unpaid`,
        { headers: { Authorization: SecurityHelper.getAuthHeader() } }
    );
    return response.data.payload;
}

export function fetchUnpaidIssuanceList(issuanceIdList, permitId) {
    return (dispatch) => {
        dispatch({
            type: types.SET_ISSUANCE_LOADING,
            issuanceLoading: true,
        });
        const issuances = issuanceIdList.map((issuanceId) =>
            axios.get(
                `${process.env.API}/permits/${permitId}/issuances/${issuanceId}/unpaid`,
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
        );

        return Promise.all(issuances)
            .then((responses) => {
                dispatch({
                    type: types.SET_ISSUANCE_LOADING,
                    issuanceLoading: false,
                });
                dispatch({
                    type: types.GET_ISSUANCE_LIST,
                    payload: responses.map((resp) => get(resp, 'data.payload', {})),
                });
            })
            .catch((err) => {
                dispatch({
                    type: types.SET_ISSUANCE_LOADING,
                    issuanceLoading: false,
                });
                if (err.response && err.response.data) {
                    dispatch({
                        type: types.GET_ISSUANCE_LIST_ERROR,
                        error: err.response.data,
                    });
                }
            });
    };
}

export function clearUnpaidIssuanceList() {
    return {
        type: types.CLEAR_ISSUANCE_LIST,
    };
}

export function fetchRecarea(recareaId) {
    return function (dispatch) {
        return axios
            .get(
                `${process.env.API}/search?fq=entity_type%3Arecarea&fq=entity_id%3A${recareaId}`
            )
            .then((success) => {
                if (success.data && success.data.size > 0) {
                    dispatch({
                        type: types.GET_RECAREA,
                        payload: Object.assign(success.data.results),
                    });
                } else {
                    const error = 'No recreation area found';
                    dispatch({ type: types.GET_RECAREA_ERROR, payload: error, error });
                }
            })
            .catch((error) => {
                dispatch({
                    type: types.GET_RECAREA_ERROR,
                    payload: 'error while fetching recarea data',
                    error,
                });
            });
    };
}

export function fetchCart() {
    return function (dispatch) {
        return axios
            .get(`${process.env.API}/cart/shoppingcart`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then((response) => {
                dispatch({ type: types.GET_CART, payload: response.data });
            })
            .catch(() => {
                dispatch({
                    type: types.GET_CART_ERROR,
                    payload: 'error fetching cart',
                    error: true,
                });
            });
    };
}

export function cancelBulkReservation(reservationIds, navigate) {
    return (dispatch) => {
        const result = reservationIds.reduce(async (prevPromise, reservationId) => {
            await prevPromise;
            return axios.delete(`${process.env.API}/cart/shoppingcart/${reservationId}`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            });
        }, Promise.resolve());

        result
            .then((response) => {
                dispatch({ type: types.CANCEL_RESERVATION, payload: response.data });
                dispatch(fetchCart());
                const redirectURL = `/permits/${types.BOUNDARY_WATERS_PERMIT_ID}/registration/detailed-availability`;
                navigate(redirectURL);
            })
            .catch(() => {
                dispatch({
                    type: types.CANCEL_RESERVATION_ERROR,
                    payload: 'error canceling reservation',
                    error: true,
                });
            });
    };
}

export function cancelReservation(reservationId, navigate) {
    return function (dispatch) {
        return axios
            .delete(`${process.env.API}/cart/shoppingcart/${reservationId}`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then((response) => {
                dispatch({ type: types.CANCEL_RESERVATION, payload: response.data });
                dispatch(fetchCart());
                const redirectURL = `/permits/${types.BOUNDARY_WATERS_PERMIT_ID}/registration/detailed-availability`;
                navigate(redirectURL);
            })
            .catch(() => {
                dispatch({
                    type: types.CANCEL_RESERVATION_ERROR,
                    payload: 'error canceling reservation',
                    error: true,
                });
            });
    };
}

export function fetchLotteryIssuances(lotteryId, statuses, alias) {
    const item = {
        lottery_id: lotteryId,
        statuses,
    };
    return function (dispatch) {
        return axios
            .post(
                `${process.env.API}/permits/0/lotteries/issuances`,
                JSON.stringify(item),
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                dispatch({
                    type: types.GET_ISSUANCE_BY_STATUS,
                    payload: { [alias]: response.data.payload },
                });
            })
            .catch(() => {
                dispatch({
                    type: types.GET_ISSUANCE_BY_STATUS_ERROR,
                    payload: 'error fetching issuances',
                    error: true,
                });
            });
    };
}

export function notifyLotteryResult(lotteryId) {
    return function (dispatch) {
        return axios
            .get(`${process.env.API}/permits/lotteries/${lotteryId}`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then(() => {
                dispatch({
                    type: types.NOTIFY_LOTTERY_RESULTS,
                    payload: 'Notify Lottery Results Success',
                });
            })
            .catch((error) => {
                dispatch({
                    type: types.NOTIFY_LOTTERY_RESULTS_ERROR,
                    payload: 'error notify lottery results',
                    error: error.response.data,
                });
            });
    };
}

export function printIssuancePDF(permitId, issuanceId) {
    return function (dispatch) {
        return axios
            .get(`${process.env.API}/permits/${permitId}/issuances/${issuanceId}/pdf`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
                responseType: 'arraybuffer',
            })
            .then((response) => {
                const blob = new Blob([response.data], { type: 'application/pdf' });
                printBlob(blob);
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_ISSUANCE_PDF_ERROR,
                        payload: 'error printing issuance PDF',
                        error: error.response.data,
                    });
                }
            });
    };
}

export function getLottery(lotteryId) {
    return function (dispatch) {
        return axios
            .get(`${process.env.API}/lottery/${lotteryId}`)
            .then((response) => {
                dispatch({ type: types.GET_LOTTERY, payload: response.data.lottery });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_LOTTERY_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                    });
                }
            });
    };
}

export function fetchCampsites(permitId) {
    return function (dispatch) {
        return axios
            .get(`${process.env.API}/permits/${permitId}/campsites`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then((response) => {
                dispatch({ type: types.GET_CAMPSITES, payload: response.data.payload });
            })
            .catch((error) => {
                dispatch({
                    type: types.GET_CAMPSITES_ERROR,
                    payload: 'error fetch campsites',
                    error: error.response.data,
                });
            });
    };
}

export function calculateFeeDifference(issuance) {
    const preparedIssuance = prepareIssuance(issuance);

    return (dispatch) =>
        axios
            .post(
                `${process.env.API}/permits/${preparedIssuance.permit_id}/issuances/${preparedIssuance.id}/fees`,
                JSON.stringify(preparedIssuance),
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                dispatch({
                    type: types.POST_ISSUANCE_CALCULATE_FEES,
                    payload: response.data.payload,
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.POST_ISSUANCE_CALCULATE_FEES_ERROR,
                        payload: 'error while calculating fee difference',
                        error: error.response.data,
                    });
                }
            });
}

export function postIssuanceCartless(issuance) {
    const preparedIssuance = prepareIssuance(issuance);

    return (dispatch) => {
        dispatch({
            type: types.SET_ISSUANCE_LOADING,
            issuanceLoading: true,
        });
        return axios
            .post(
                `${process.env.API}/permits/${preparedIssuance.permit_id}/issuances/${preparedIssuance.id}/cartless`,
                JSON.stringify(preparedIssuance),
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                dispatch({
                    type: types.SET_ISSUANCE_LOADING,
                    issuanceLoading: false,
                });
                dispatch({
                    type: types.POST_ISSUANCE_CARTLESS,
                    payload: response.data.payload,
                });
            })
            .catch((error) => {
                dispatch({
                    type: types.SET_ISSUANCE_LOADING,
                    issuanceLoading: false,
                });
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.POST_ISSUANCE_CARTLESS_ERROR,
                        payload: 'error while posting issuance without payment',
                        error: error.response.data,
                    });
                }
            });
    };
}

export function bypassToOrderConfirmation(issuance, permit) {
    const division = getIssuanceDivision(issuance, permit);
    const payload = {
        order_id: issuance.order_id,
        completed_reservations: [
            {
                order_details: {
                    description: permit.name,
                    img_url: permit.img_url,
                    location: division ? division.name : '',
                },
                order_id: issuance.order_id,
            },
        ],
        subtotal: 0,
        tax: 0,
        total: 0,
    };

    return (dispatch) => {
        dispatch({ type: types.FINALIZE_CHECKOUT, payload });
    };
}

export function clearIssuanceFromStore() {
    return (dispatch) => {
        dispatch({ type: types.CLEAR_ISSUANCE });
    };
}

export function clearExistingIssuance() {
    return (dispatch) => {
        dispatch({ type: types.CLEAR_EXISTING_ISSUANCE });
    };
}

export function formValidationChange(path, error) {
    return (dispatch) => {
        dispatch({
            type: types.SET_VALIDATION_ERROR,
            payload: { path, error },
        });
    };
}

export function setPermitType(value) {
    return {
        type: types.SET_PERMIT_TYPE,
        permitType: value,
    };
}

export function setPermitDate(value) {
    return {
        type: types.SET_PERMIT_DATE,
        permitDate: value,
    };
}

export function setSelectedDivision(division) {
    return {
        type: types.SET_SELECTED_DIVISION,
        division,
    };
}

export function setSelectedStartDate(value) {
    return {
        type: types.SET_SELECTED_START_DATE,
        date: value,
    };
}

export function setSelectedEndDate(value) {
    return {
        type: types.SET_SELECTED_END_DATE,
        date: value,
    };
}

export function setSelectedDivision2(division) {
    return {
        type: types.SET_SELECTED_DIVISION2,
        division,
    };
}

export function setSelectedStartDate2(value) {
    return {
        type: types.SET_SELECTED_START_DATE2,
        date: value,
    };
}

export function setSelectedEndDate2(value) {
    return {
        type: types.SET_SELECTED_END_DATE2,
        date: value,
    };
}

export function setQuestionnaireAnswers(answers) {
    return {
        type: types.SET_QUESTIONNAIRE_ANSWERS,
        answers,
    };
}

export function setActiveTab(value) {
    return {
        type: types.SET_ACTIVE_TAB,
        activeTab: value,
    };
}

export function setDivisionSearchCriteria(criteria) {
    return {
        type: types.SET_DIV_SEARCH_CRITERIA,
        divisionSearchCriteria: criteria,
    };
}

export function setExistingIssuance(value) {
    return {
        type: types.SET_EXISTING_ISSUANCE,
        existingIssuance: value,
    };
}

export function clearIssuanceErrors() {
    return {
        type: types.SET_CLEAR_ISSUANCE_ERRORS,
    };
}

export function openDivisionDetailsModal(divisionId) {
    return {
        type: types.OPEN_DIVISION_DETAILS_MODAL,
        payload: divisionId,
    };
}

export function closeDivisionDetailsModal() {
    return {
        type: types.CLOSE_DIVISION_DETAILS_MODAL,
    };
}
